import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { BookOpen, AlertCircle, PowerOff, Heart } from 'lucide-react'; // Added PowerOff and Heart icons
import { ApiService } from './services/api'; // Adjusted import path
import { Channel } from './types'; // Adjusted import path
import { LoadingSpinner } from './components/LoadingSpinner'; // Assuming this component exists
import { useAuth } from './AuthContext'; // Added auth context
import { Navigation } from './components/Navigation';
import { ThemeToggle } from './components/ThemeToggle';
import { toast } from 'react-hot-toast';

const CHANNELS_PER_PAGE = 20;

const languageCodeToName: { [key: string]: string } = {
  en: 'English',
  es: 'Español',
  ko: '한국어 (Korean)',
  ja: '日本語 (Japanese)',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  ru: 'Русский',
  zh: '中文 (Chinese)',
  id: 'Bahasa Indonesia',
  hi: 'हिन्दी (Hindi)',
  tl: 'Tagalog',
  hil: 'Hiligaynon',
  ar: 'العربية (Arabic)',
  it: 'Italiano',
  lus: 'Mizo',
  my: 'Burmese (မြန်မာဘာသာ)',
  th: 'Thai (ภาษาไทย)',
  uzn: 'Uzbek (Oʻzbekcha)',
  vi: 'Vietnamese (Tiếng Việt)',
  // Add more as needed
};

const getLanguageName = (code: string | null): string => {
  if (!code) return 'N/A';
  return languageCodeToName[code.toLowerCase()] || code;
};

const ChannelsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth(); // Added auth context

  // Debug: Log user info
  console.log('ChannelsPage - User:', user);
  console.log('ChannelsPage - isAuthenticated:', isAuthenticated);
  console.log('ChannelsPage - is_admin:', user?.is_admin);

  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalChannels, setTotalChannels] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [deactivatingChannel, setDeactivatingChannel] = useState<number | null>(null);
  const [favouritingChannel, setFavouritingChannel] = useState<number | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const fetchInProgress = React.useRef<boolean>(false);

  // Debug: Log first channel data to check is_favourited
  useEffect(() => {
    if (channels.length > 0) {
      console.log('First channel data:', channels[0]);
      console.log('is_favourited type:', typeof channels[0].is_favourited);
      console.log('is_favourited value:', channels[0].is_favourited);
    }
  }, [channels]);

  // Debug: Log user object to check admin status
  useEffect(() => {
    if (user) {
      console.log('User object:', user);
      console.log('is_admin type:', typeof user.is_admin);
      console.log('is_admin value:', user.is_admin);
      console.log('Boolean(is_admin):', Boolean(user.is_admin));
    }
  }, [user]);

  // Get language from URL params, default to empty string for 'All Languages'
  const [selectedLanguage, setSelectedLanguage] = useState<string>(searchParams.get('language') || '');
  const [sortBy, setSortBy] = useState<string>('name'); // Default sort: name
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc'); // Default order: asc

  const fetchChannels = useCallback(async (currentPage: number, clearPrevious = false) => {
    // Prevent multiple simultaneous requests
    if (fetchInProgress.current) return;
    
    fetchInProgress.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const apiCallParams: any = {
        active: true,
        page: currentPage,
        limit: CHANNELS_PER_PAGE,
        sortBy,
        sortOrder,
      };
      
      if (selectedLanguage) {
        apiCallParams.language = selectedLanguage;
      }
      
      if (isAuthenticated && user?.id) {
        apiCallParams.userId = user.id;
      }
      
      const response = await ApiService.getChannels(apiCallParams);
      
      setChannels(prevChannels => {
        // If this is a refresh or first page, replace all channels
        if (clearPrevious || currentPage === 1) {
          return response.data;
        }
        
        // Create a map of existing channel IDs for O(1) lookup
        const existingIds = new Set(prevChannels.map(ch => ch.id));
        // Filter out any channels that already exist
        const newChannels = response.data.filter(ch => !existingIds.has(ch.id));
        
        // If no new channels, we've reached the end
        if (newChannels.length === 0) {
          setHasMore(false);
          return prevChannels;
        }
        
        return [...prevChannels, ...newChannels];
      });
      
      setTotalChannels(response.total);
      setHasMore(response.hasMore);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch channels';
      setError(`Error: ${errorMessage}. Please try again.`);
      console.error('Fetch channels error:', err);
      
      // Reset pagination on error
      if (currentPage > 1) {
        setPage(1);
      }
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, [selectedLanguage, sortBy, sortOrder, isAuthenticated, user?.id]);

  useEffect(() => {
    // Fetch available languages once on mount
    const fetchLanguages = async () => {
      try {
        const langs = await ApiService.getLanguages();
        setAvailableLanguages(langs);
      } catch (err) {
        console.error('Failed to fetch languages:', err);
        // Optionally set an error state for languages
      }
    };
    fetchLanguages();
  }, []);

  // Sync page from URL params on mount and when search params change
  useEffect(() => {
    const pageParam = searchParams.get('page');
    const languageParam = searchParams.get('language');
    const sortByParam = searchParams.get('sortBy');
    const sortOrderParam = searchParams.get('sortOrder');
    
    let shouldUpdate = false;
    
    // Check if we need to update the page
    if (pageParam) {
      const pageNum = parseInt(pageParam, 10);
      if (!isNaN(pageNum) && pageNum > 0 && pageNum !== page) {
        setPage(pageNum);
        shouldUpdate = true;
      }
    }
    
    // Check if we need to update the language
    if (languageParam !== null && languageParam !== selectedLanguage) {
      setSelectedLanguage(languageParam);
      shouldUpdate = true;
    }
    
    // Check if we need to update sortBy
    if (sortByParam && sortByParam !== sortBy) {
      setSortBy(sortByParam);
      shouldUpdate = true;
    }
    
    // Check if we need to update sortOrder
    if (sortOrderParam && (sortOrderParam === 'asc' || sortOrderParam === 'desc') && sortOrderParam !== sortOrder) {
      setSortOrder(sortOrderParam);
      shouldUpdate = true;
    }
    
    // If we're on page 1 and no other changes, force a refresh
    if (!shouldUpdate && page === 1) {
      fetchChannels(1, true);
    }
  }, [searchParams, page, selectedLanguage, sortBy, sortOrder, fetchChannels]);

  useEffect(() => {
    // Only fetch if not already fetching
    if (!isFetching) {
      const isFirstPage = page === 1;
      fetchChannels(page, isFirstPage);
    }
    
    // Update URL with current page for SEO (crawlable pagination)
    const newSearchParams = new URLSearchParams(searchParams);
    if (page > 1) {
      newSearchParams.set('page', page.toString());
    } else {
      newSearchParams.delete('page');
    }
    // Preserve language filter
    if (selectedLanguage) {
      newSearchParams.set('language', selectedLanguage);
    } else {
      newSearchParams.delete('language');
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [fetchChannels, page, isFetching, selectedLanguage, sortBy, sortOrder]);

  
  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Change field and reset to asc
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1); // Reset to first page when sorting
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    setPage(1); // Reset to first page
    setChannels([]); // Clear existing channels immediately for better UX

    // Update URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    if (newLanguage) {
      newSearchParams.set('language', newLanguage);
    } else {
      newSearchParams.delete('language');
    }
    setSearchParams(newSearchParams);
  };

  const handleSortByChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(event.target.value);
    setPage(1); // Reset to first page
    setChannels([]); // Clear existing channels
  };

  const handleSortOrderToggle = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
    setPage(1); // Reset to first page
    setChannels([]); // Clear existing channels
  };

  const handleDeactivateChannel = async (channelId: number, channelName: string) => {
    if (!confirm(`Are you sure you want to deactivate "${channelName}"? This will hide it from the channel list.`)) {
      return;
    }

    setDeactivatingChannel(channelId);
    try {
      await ApiService.deactivateChannel(channelId);

      // Remove the channel from the current list
      setChannels(prev => prev.filter(channel => channel.id !== channelId));
      setTotalChannels(prev => prev - 1);

      toast.success(`Channel "${channelName}" has been deactivated successfully.`);
    } catch (error) {
      console.error('Error deactivating channel:', error);
      toast.error('Failed to deactivate channel. Please try again.');
    } finally {
      setDeactivatingChannel(null);
    }
  };

  const handleToggleFavourite = async (channelId: number, channelName: string, isCurrentlyFavourited: boolean) => {
    if (!isAuthenticated) {
      toast('Please log in to add channels to your favourites! ❤️', {
        icon: '❤️',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      return;
    }

    setFavouritingChannel(channelId);
    try {
      if (isCurrentlyFavourited) {
        await ApiService.removeFromFavourites(channelId);
        // Update the channel in the list to show it's no longer favourited
        setChannels(prev => prev.map(channel =>
          channel.id === channelId ? { ...channel, is_favourited: false } : channel
        ));
      } else {
        await ApiService.addToFavourites(channelId);
        // Update the channel in the list to show it's now favourited
        setChannels(prev => prev.map(channel =>
          channel.id === channelId ? { ...channel, is_favourited: true } : channel
        ));
      }
    } catch (error) {
      console.error('Error toggling favourite:', error);
      toast.error('Failed to update favourite status. Please try again.');
    } finally {
      setFavouritingChannel(null);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      <header className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4 lg:gap-6">
              <Link to="/" className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-purple-600 rounded-lg">
                  <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">ManhwaRecap</h1>
                </div>
              </Link>
              <Navigation />
            </div>
            <div className="flex items-center space-x-1 md:space-x-2 lg:space-x-4">
              <div className="hidden sm:block text-sm text-gray-600 dark:text-gray-400">
                {totalChannels.toLocaleString()} Channels
              </div>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Browse Channels</h2>
          {availableLanguages.length > 0 && (
            <div className="mt-4 sm:mt-0">
              <label htmlFor="language-filter" className="sr-only">Filter by language</label>
              <select
                id="language-filter"
                value={selectedLanguage}
                onChange={handleLanguageChange}
                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 p-2.5 min-w-[150px]"
              >
                <option value="">All Languages</option>
                {availableLanguages.map(langCode => (
                  <option key={langCode} value={langCode}>
                    {getLanguageName(langCode)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Sorting Controls */}
          <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2 items-center">
            <div>
              <label htmlFor="sort-by" className="sr-only">Sort by</label>
              <select
                id="sort-by"
                value={sortBy}
                onChange={handleSortByChange}
                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 p-2.5 min-w-[150px]"
              >
                <option value="name">Name</option>
                <option value="total_videos">Most Videos</option>
                <option value="subscriber_count">Most Subscribers</option>
                <option value="last_posted">Last Posted</option>
                <option value="total_views">Most Views</option>
                <option value="total_likes">Most Likes</option>
              </select>
            </div>
            <button
              onClick={handleSortOrderToggle}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 p-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              title={`Sort order: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
            >
              {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
          </div>
        </div>

        {error && (
          <div className="text-center py-12 bg-gray-100 dark:bg-gray-800 p-6 rounded-lg shadow-xl">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Error loading channels</h3>
            <p className="text-gray-600 dark:text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => fetchChannels(1, true)} // Reset to page 1 on retry, clear previous
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        )}

        {loading && channels.length === 0 && !error && <LoadingSpinner />}

        {!loading && channels.length === 0 && !error && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No channels found</h3>
            <p className="text-gray-600 dark:text-gray-500">No active channels available at the moment.</p>
          </div>
        )}

        {channels.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th 
                    onClick={() => handleSort('name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortBy === 'name' && (
                        <span className="text-purple-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('total_videos')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <div className="flex items-center gap-1">
                      Videos
                      {sortBy === 'total_videos' && (
                        <span className="text-purple-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('subscriber_count')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <div className="flex items-center gap-1">
                      Subscribers
                      {sortBy === 'subscriber_count' && (
                        <span className="text-purple-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('total_views')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <div className="flex items-center gap-1">
                      Views
                      {sortBy === 'total_views' && (
                        <span className="text-purple-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('total_likes')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <div className="flex items-center gap-1">
                      Likes
                      {sortBy === 'total_likes' && (
                        <span className="text-purple-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('last_posted')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <div className="flex items-center gap-1">
                      Last Post
                      {sortBy === 'last_posted' && (
                        <span className="text-purple-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('language')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <div className="flex items-center gap-1">
                      Language
                      {sortBy === 'language' && (
                        <span className="text-purple-400">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {channels.map(channel => (
                  <tr key={channel.id} className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={`/media/thumbnails/${channel.id}.jpg`}
                            alt={`${channel.name} thumbnail`}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <div className="h-10 w-10 rounded-full bg-gray-600 flex items-center justify-center hidden">
                            <BookOpen className="h-6 w-6 text-gray-400" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <Link 
                            to={`/recaps/channel/${channel.id}${channel.language ? `?language=${channel.language}` : ''}`}
                            className="text-sm font-medium text-purple-400 hover:text-purple-300"
                          >
                            {channel.name}
                          </Link>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      {channel.total_videos.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      {channel.subscriber_count ? channel.subscriber_count.toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      <div className="flex items-center gap-1">
                        <span>👁️</span>
                        {channel.total_views ? channel.total_views.toLocaleString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      <div className="flex items-center gap-1">
                        <span>❤️</span>
                        {channel.total_likes ? channel.total_likes.toLocaleString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      {channel.last_posted ? new Date(channel.last_posted).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700">
                        {getLanguageName(channel.language)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleFavourite(channel.id, channel.name, Boolean(channel.is_favourited))}
                          disabled={favouritingChannel === channel.id}
                          className={`p-1.5 rounded transition-colors ${isAuthenticated && Boolean(channel.is_favourited)
                            ? 'text-red-400 hover:text-red-300'
                            : 'text-gray-400 hover:text-red-400'
                          }`}
                          title={
                            isAuthenticated
                              ? (Boolean(channel.is_favourited) ? `Remove ${channel.name} from favourites` : `Add ${channel.name} to favourites`)
                              : 'Login to add to favourites! ❤️'
                          }
                        >
                          <Heart className={`w-4 h-4 ${isAuthenticated && Boolean(channel.is_favourited) ? 'fill-current' : ''}`} />
                        </button>
                        {isAuthenticated && Boolean(user?.is_admin) && (
                          <button
                            onClick={() => handleDeactivateChannel(channel.id, channel.name)}
                            disabled={deactivatingChannel === channel.id}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors border border-red-500/30 hover:border-red-400/50"
                            title={`Deactivate ${channel.name}`}
                          >
                            <PowerOff className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasMore && !loading && (
          <div className="text-center mt-8">
            <Link
              to={`/channels?page=${page + 1}${selectedLanguage ? `&language=${selectedLanguage}` : ''}`}
              className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200"
            >
              Next Page →
            </Link>
          </div>
        )}
        {loading && channels.length > 0 && (
          <div className="text-center mt-8">
            <LoadingSpinner />
          </div>
        )}

        <p className="text-gray-600 dark:text-gray-400 mt-8">
          <Link to="/" className="text-purple-400 hover:underline">Back to Recaps</Link>
        </p>
      </main>
    </div>
  );
};

export default ChannelsPage;
